import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Heading } from "../../components";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import LeadCaptureModal from "components/LeadCaptureModal";
import { BENCHMARKS } from "./benchmarkData";
import { BarChart, ScatterChart } from "./charts";
import "./style.css";

function scrollToId(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function BenchmarkSection({ benchmark, onRequestReport }) {
    const scatterPoints = benchmark.rows.filter((row) => typeof row.latencyMs === "number");

    return (
        <section className="benchmarks-section" id={benchmark.id}>
            <div className="benchmarks-section-head">
                <span>{benchmark.eyebrow}</span>
                <Heading size="xl" as="h2">{benchmark.title}</Heading>
            </div>

            <div className={`benchmarks-panels ${benchmark.kind === "bar+scatter" ? "" : "single"}`}>
                <div className="benchmarks-panel">
                    <BarChart rows={benchmark.rows} unit={benchmark.unit} />
                </div>
                {benchmark.kind === "bar+scatter" && (
                    <div className="benchmarks-panel">
                        <ScatterChart points={scatterPoints} />
                    </div>
                )}
            </div>

            <div className="benchmarks-methodology">
                <p>{benchmark.methodology.intro}</p>
                <p>
                    {benchmark.methodology.datasetNote}
                    {benchmark.methodology.sourceHref && (
                        <>
                            {" "}Source:{" "}
                            <a href={benchmark.methodology.sourceHref} target="_blank" rel="noopener noreferrer">
                                {benchmark.methodology.sourceLabel}
                            </a>
                            .
                        </>
                    )}
                </p>
            </div>

            {benchmark.secondaryCta && (
                <div className="benchmarks-section-cta">
                    <Link to={benchmark.secondaryCta.href}>{benchmark.secondaryCta.label} →</Link>
                </div>
            )}
        </section>
    );
}

export default function BenchmarksPage() {
    const [modalOpen, setModalOpen] = useState(false);

    return (
        <>
            <Helmet>
                <title>Benchmarks — AIHumanity vs LLMs &amp; Open-Source Models</title>
                <meta
                    name="description"
                    content="Reproducible accuracy and speed benchmarks: AIHumanity's emotion model against frontier LLMs and popular open-source emotion recognition models, sourced from AIMultiple and internal evaluation."
                />
            </Helmet>

            <div className="benchmarks-page">
                <TopMenuBar />

                <section className="benchmarks-hero">
                    <span className="benchmarks-badge">Model Performance</span>
                    <Heading size="3xl" as="h1" className="!text-transparent bg-gradient5 bg-clip-text">
                        Proof, not promises
                    </Heading>
                    <p>
                        Every number on this page traces to a reproducible, third-party or independently-run
                        evaluation — not an internal claim. Built for engineers deciding what to integrate,
                        and for investors evaluating the moat.
                    </p>

                    <div className="benchmarks-stat-strip">
                        <div className="benchmarks-stat">
                            <strong className="!text-transparent bg-gradient5 bg-clip-text">90%</strong>
                            <span>vs. 69% for the best LLM tested</span>
                        </div>
                        <div className="benchmarks-stat">
                            <strong className="!text-transparent bg-gradient5 bg-clip-text">85.1%</strong>
                            <span>vs. 63.6% for the best open-source model</span>
                        </div>
                    </div>

                    <div className="benchmarks-hero-actions">
                        <button type="button" className="primary" onClick={() => scrollToId(BENCHMARKS[0].id)}>
                            See the methodology
                        </button>
                        <button type="button" className="secondary" onClick={() => setModalOpen(true)}>
                            Request the full benchmark report
                        </button>
                    </div>

                    <nav className="benchmarks-jumpnav" aria-label="Jump to benchmark">
                        {BENCHMARKS.map((benchmark) => (
                            <button key={benchmark.id} type="button" onClick={() => scrollToId(benchmark.id)}>
                                {benchmark.title}
                            </button>
                        ))}
                    </nav>
                </section>

                {BENCHMARKS.map((benchmark) => (
                    <BenchmarkSection key={benchmark.id} benchmark={benchmark} />
                ))}

                <section className="benchmarks-closing-cta">
                    <Heading size="2xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text max-w-[600px] mx-auto mb-6">
                        Want the numbers behind your own data?
                    </Heading>
                    <p>
                        Get the full benchmark report — methodology, per-class breakdowns, and how the
                        same model performs on a domain-tuned version of your data.
                    </p>
                    <button type="button" onClick={() => setModalOpen(true)}>
                        Request the full benchmark report →
                    </button>
                </section>

                <FooterSection />
            </div>

            <LeadCaptureModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                kind="contact"
                context="Requesting the full facial-emotion + open-source benchmark report"
                successMessage="Thanks — we'll email the full report (raw data, per-class breakdowns, and methodology) to the address you provided within 2 business days."
            />
        </>
    );
}
